import { doc } from "firebase/firestore";
import { auth, db } from "@/services/firebaseService";
import { createCampPhoto, addPhotoToLocationVersion } from "@/services/campPhotoService";
import { syncCampPhotoUrlsAfterUpload } from "@/services/syncCampPhotoUrlsService";

export type CampPhotoImportAttribution = {
  photoReference: string;
  permissionStatus: "granted";
  permissionNote: string;
  permissionInternalNote: string;
};

export async function createAndAttachCampPhotoFromStorageUpload(input: {
  locationId: string;
  organizationId: string;
  storagePath: string;
  downloadUrl: string;
  yOffset: number;
  caption?: string;
  altText?: string;
  attribution: CampPhotoImportAttribution;
  locationAccessGroupId?: string;
}) {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) throw new Error("Authentication required");

  const photoData: any = {
    path: input.storagePath,
    photoReference: input.attribution.photoReference,
    permissionStatus: "granted" as const,
    permissionNote: input.attribution.permissionNote,
    permissionInternalNote: input.attribution.permissionInternalNote,
    caption: input.caption || "",
    altText: input.altText || "",
    yOffset: input.yOffset || 0,
    organizationId: input.organizationId,
    createdBy: doc(db, "users", firebaseUser.uid),
  };
  if (input.locationAccessGroupId) {
    photoData.locationAccessGroupId = input.locationAccessGroupId;
  }

  const { photoDoc } = await createCampPhoto(photoData, input.locationId, firebaseUser.uid);

  await addPhotoToLocationVersion(input.locationId, firebaseUser.uid, {
    path: input.downloadUrl,
    photoReference: input.attribution.photoReference,
    caption: input.caption || "",
    altText: input.altText || "",
    yOffset: input.yOffset || 0,
    campPhotoId: photoDoc,
    permissionStatus: "granted" as const,
  });

  try {
    for (let attempt = 0; attempt < 5; attempt++) {
      const r = await syncCampPhotoUrlsAfterUpload(photoDoc.id);
      if (r.propagated) break;
      if (r.reason !== "derivatives_not_ready") break;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  } catch {
    // imageOnFinalize late propagation / Firestore trigger still converge; do not fail upload UX
  }

  return { photoDoc };
}

export async function buildProviderPermissionInternalNote(params: {
  provider: "Unsplash";
  providerProfileUrl: string;
  providerPhotoId: string;
  providerPhotoPageUrl: string;
  assertedByEmail: string;
  assertedByUid: string;
  assertedAtIso: string;
  ipAddress: string;
}): Promise<string> {
  const p = params;
  // Keep as a compact, parseable sentence for future linking.
  return [
    `${p.assertedByEmail} (${p.assertedByUid}) at ${p.assertedAtIso} from IP ${p.ipAddress} asserts photo is from ${p.provider}.`,
    `Photographer profile: ${p.providerProfileUrl}`,
    `Unsplash photo: ${p.providerPhotoPageUrl}`,
    `Unsplash photo id: ${p.providerPhotoId}`,
  ].join(" ");
}

export function easternTimestampIsoNoMs(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}:${get("second")}`;
}

export async function bestEffortIp(): Promise<string> {
  try {
    const r = await fetch("https://api.ipify.org?format=json");
    const j: any = await r.json();
    return String(j?.ip || "unknown");
  } catch {
    return "unknown";
  }
}

